import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useLocation, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {getInvoiceDiscrepancies} from '../ducks/invoiceDiscrepancies';

interface InvoiceRouteProps {
  accountNumber: string;
  invoiceId: string;
  revision: string;
  uuid: string;
}

type InvoiceDiscrepanciesProps = RouteComponentProps<InvoiceRouteProps>;

const getInvoiceDiscrepanciesRequestPayload = (params: InvoiceRouteProps) => {
  /*
    EIS Invoice Link Format:
    gpart://Invoice/Invoicing//<invoiceID>/<rev>/<invoiceID>/<uuid>
  */
  const {accountNumber, invoiceId, revision, uuid} = params;
  return {
    accountNumber: accountNumber,
    invoiceLink: `gpart://Invoice/Invoicing//${invoiceId}/${revision}/${invoiceId}/${uuid}`,
    uuid,
  };
};

const InvoiceDiscrepancies = (_props: InvoiceDiscrepanciesProps) => {
  const match = useRouteMatch<InvoiceRouteProps>();
  const dispatch = useDispatch();
  const location = useLocation();
  const {invoiceDiscrepancies} = useSelector((state: ReducerState) => state.invoiceDiscrepancies);
  const {accountNumber, invoiceLink} = getInvoiceDiscrepanciesRequestPayload(match.params);

  const returnUrl = location.search ?? '';

  useBreadcrumbs(accountNumber, '/agent/accounts/' + accountNumber + '/accountNumber/' + accountNumber + returnUrl);

  useEffect(() => {
    dispatch(getInvoiceDiscrepancies(accountNumber, invoiceLink));
  }, [accountNumber, invoiceLink, dispatch]);

  return (
    <PagedView
      pagedViewKey="invoiceDiscrepancies"
      loadMore={page => dispatch(getInvoiceDiscrepancies(accountNumber, invoiceLink, page))}
      data={invoiceDiscrepancies}
    >
      {columnProps => (
        <>
          <Column {...columnProps} title="Policy Number" default ddprivacy="mask">
            {discrepancy => discrepancy.policyNumber}
          </Column>
          <Column {...columnProps} title="Insured Name" default ddprivacy="mask">
            {discrepancy => discrepancy.insuredName}
          </Column>
          <Column {...columnProps} title="Bill Amount" default>
            {discrepancy => toMoneyValue(discrepancy.billedAmount)}
          </Column>
          <Column {...columnProps} title="Paid Amount" default>
            {discrepancy => toMoneyValue(discrepancy.paidAmount)}
          </Column>
          <Column {...columnProps} title="Balance Due" default>
            {discrepancy => toMoneyValue(discrepancy.discrepancyAmount)}
          </Column>
          <Column {...columnProps} title="Issue Date" default>
            {discrepancy => toFormattedDateAsUTC(discrepancy.issueDate)}
          </Column>
          <Column {...columnProps} title="Product Type" default>
            {discrepancy => discrepancy.productType}
          </Column>
          <Column {...columnProps} title="Status" default>
            {discrepancy =>
              discrepancy.state
                ?.split(' ')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(' ')
            }
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default InvoiceDiscrepancies;
